import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Table, Segmented, Tag, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import AnalyticsPage from '../../shared-components/analytics/AnalyticsPage';
import QualityNote from '../../shared-components/analytics/QualityNote';
import StatTile from '../../shared-components/analytics/StatTile';
import BarList from '../../shared-components/charts/BarList';
import { getShrinkageSummary, getShrinkageVsSales } from '../../../analytics/analyticsApi';
import { thousand, toCurrency } from '../../../functions';
import { status } from '../../../analytics/vizTokens';

const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}%`);

// Fresh departments typically run 2-5%; past that is worth flagging. Status
// colour never carries this alone -- the row also gets an icon and a label.
function rateTone(rate) {
  if (rate == null) return null;
  if (rate >= 5) return { color: status.critical, label: 'analytics_rate_high', icon: faTriangleExclamation };
  if (rate >= 2) return { color: status.warning, label: 'analytics_rate_watch', icon: faCircleInfo };
  return null;
}

function AnalyticsShrinkage() {
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState(0);
  const [summary, setSummary] = useState(null);
  const [vsSales, setVsSales] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([getShrinkageSummary({ groupBy }), getShrinkageVsSales({})])
      .then(([s, v]) => { setSummary(s); setVsSales(v); setLoading(false); })
      .catch(() => setLoading(false));
  }, [groupBy]);

  const rows = summary?.items ?? [];
  const vsRows = vsSales?.items ?? [];
  // absoluteCostValue exists so charts never have to Math.abs a negative and risk
  // the positive value being persisted somewhere.
  const totalAbs = _.sumBy(rows, 'absoluteCostValue');
  const worst = _.maxBy(vsRows.filter((r) => r.shrinkageRatePct != null), 'shrinkageRatePct');
  const orphans = vsRows.filter((r) => r.hasShrinkageWithoutSales);

  return (
    <AnalyticsPage
      title={t('analytics_shrinkage_title')}
      subtitle={t('analytics_shrinkage_subtitle')}
    >
      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={t('analytics_total_loss')}
          value={toCurrency(totalAbs)}
          emphasis
          hint={t('analytics_loss_help')}
        />
        <StatTile
          label={t('analytics_movements')}
          value={thousand(_.sumBy(rows, 'movementCount'))}
        />
        <StatTile
          label={t('analytics_worst_department')}
          value={worst ? pct(worst.shrinkageRatePct) : '—'}
          sub={worst?.name}
        />
        <StatTile
          label={t('analytics_loss_without_sales')}
          value={thousand(orphans.length)}
          hint={t('analytics_orphan_help')}
        />
      </div>

      {orphans.length > 0 && (
        <div className="mb-5 rounded-card border border-danger/30 bg-rose-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 text-danger" />
            <div>
              <div className="text-sm font-semibold text-danger">
                {t('analytics_orphan_title')}
              </div>
              <div className="mt-0.5 text-xs leading-relaxed text-ink">
                {t('analytics_orphan_body')} — {orphans.map((o) => o.name).join(', ')}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-card border border-line bg-surface p-4 shadow-card">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm font-semibold text-ink">{t('analytics_loss_by')}</span>
            <Segmented
              size="small"
              value={groupBy}
              onChange={setGroupBy}
              options={[
                { label: t('analytics_reason'), value: 0 },
                { label: t('analytics_department'), value: 1 },
                { label: t('analytics_store'), value: 3 },
              ]}
            />
          </div>
          <BarList
            rows={_.map(rows, (r) => ({ id: r.key, label: r.name, value: r.absoluteCostValue }))}
            formatValue={(v) => toCurrency(v)}
            renderTooltip={(row) => {
              const full = _.find(rows, (r) => r.key === row.id);
              return (
                <div className="text-xs tabular-nums" style={{ minWidth: '13rem' }}>
                  <div className="mb-1 font-semibold">{full?.name}</div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_cost_value')}</span>
                    <span>{toCurrency(full?.movementCostValue)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_units')}</span>
                    <span>{thousand(full?.movementQuantity)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_movements')}</span>
                    <span>{thousand(full?.movementCount)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_share')}</span>
                    <span>{pct(full?.shareOfTotalPct)}</span>
                  </div>
                </div>
              );
            }}
          />
          <p className="mt-4 border-t border-line pt-3 text-xs leading-relaxed text-muted">
            {t('analytics_transfer_note')}
          </p>
        </div>

        <div className="rounded-card border border-line bg-surface p-4 shadow-card">
          <div className="mb-3 text-sm font-semibold text-ink">
            {t('analytics_shrinkage_vs_sales')}
          </div>
          <Table
            size="small"
            bordered={false}
            loading={loading}
            rowKey="key"
            pagination={false}
            scroll={{ x: 'max-content' }}
            dataSource={vsRows}
            locale={{ emptyText: t('no_data') }}
            columns={[
              {
                title: t('analytics_department'),
                key: 'name',
                render: (r) => (
                  <span className="inline-flex items-center gap-2">
                    <span className="text-sm font-semibold text-ink">{r.name}</span>
                    {r.hasShrinkageWithoutSales && (
                      <Tag color="error" className="m-0">{t('analytics_no_sales')}</Tag>
                    )}
                  </span>
                ),
              },
              {
                title: t('analytics_rate'),
                key: 'rate',
                align: 'right',
                render: (r) => {
                  const tone = rateTone(r.shrinkageRatePct);
                  return (
                    <span className="inline-flex items-center gap-1.5 tabular-nums font-semibold">
                      {tone && (
                        <FontAwesomeIcon
                          icon={tone.icon}
                          style={{ color: tone.color, fontSize: '0.75rem' }}
                        />
                      )}
                      <span style={{ color: tone ? tone.color : undefined }}>
                        {pct(r.shrinkageRatePct)}
                      </span>
                      {tone && (
                        <span className="text-[10px] font-medium uppercase" style={{ color: tone.color }}>
                          {t(tone.label)}
                        </span>
                      )}
                    </span>
                  );
                },
              },
              {
                title: t('analytics_loss_at_cost'),
                key: 'loss',
                align: 'right',
                render: (r) => (
                  <span className="tabular-nums">{toCurrency(Math.abs(r.shrinkageCostValue))}</span>
                ),
              },
              {
                title: t('analytics_sales_at_cost'),
                key: 'salesCost',
                align: 'right',
                render: (r) => <span className="tabular-nums text-muted">{toCurrency(r.salesCost)}</span>,
              },
            ]}
          />
          <p className="mt-3 text-xs leading-relaxed text-muted">
            {t('analytics_rate_basis_note')}
          </p>
          <QualityNote quality={summary?.quality} className="mt-3 border-t border-line pt-3" />
        </div>
      </div>
    </AnalyticsPage>
  );
}

export default AnalyticsShrinkage;
